//U1 connect and disconnect wallet
import detectEthereumProvider from '@metamask/detect-provider';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { networkConfig, networkId } from 'config';
import { ethers } from 'ethers';
import { getContract } from 'ultils';

const SC_OWNER_ADDRESS = process.env.REACT_APP_SC_OWNER_ADDRESS as string;
const ORACLE_ADDRESS = process.env.REACT_APP_ORACLE_ADDRESS as string;

export type SystemState = {
  name: string;
  connectedUser?: string;
  isInstalled: boolean;
  smartContractOwner: string;
  oracle: string;
  loadingAtBegining: boolean;
};

const initialState: SystemState = {
  name: "systemState",
  connectedUser: undefined,
  isInstalled: false,
  smartContractOwner: SC_OWNER_ADDRESS,
  oracle: ORACLE_ADDRESS,
  loadingAtBegining: true,
};

export const switchNetwork = async () => {
  try {
    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: networkId }],
    });
    return true;
  } catch (switchError: any) {
    if (switchError.code === 4902) {
      try {
        await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [networkConfig],
        });
      } catch (error) {
        console.log("Error when changing network: ", error);
      }
    } else {
      alert("Failed to switch to the network");
    }
    return false;
  }
};

//U1 check whether user has wallet installed
export const checkConnectWallet = createAsyncThunk(
  "systemState/checkConnectWallet",
  async () => {
    try {
      const provider = await detectEthereumProvider() as ethers.providers.Web3Provider;

      if(!provider) {
        return false;
      }

      const chainId = await provider.send("eth_chainId", []);

      if (chainId !== networkId) {
        await switchNetwork();
      }

      if (provider !== window.ethereum) {
        return false;
      }
      return true;
    } catch (error) {
      console.log('Error when detecting or switching ethereum provider: ', error);
    }

    return false;
  }
);

//get oracle and smart contract owner from smart contract
export const getSmartContractAccounts = createAsyncThunk(
  "systemState/getSmartContractOwner",
  async () => {
    const contract = getContract();
    if (contract) {
      try {
        const listAccounts: string[] = await Promise.all([
          contract.owner(),
          contract.oracle(),
        ]);
        return listAccounts;
      } catch (err) {
        console.log(err);
        return [];
      }
    }
    return "";
  }
);

//U1 get logged account
export const getConnectedUser = createAsyncThunk(
  "systemState/getConnectedUser",
  async () => {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    if (provider) {
      try {
        const accounts = await provider.send("eth_accounts", []);
        return accounts[0];
      } catch (err) {
        return undefined;
      }
    }
    return undefined;
  }
);

//U1 send a request to connect to wallet
export const connectWallet = createAsyncThunk(
  "systemState/connectWallet",
  async () => {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    if (provider) {
      try {
        const accounts = await provider.send("eth_requestAccounts", []);
        return accounts[0];
      } catch (err) {
        return undefined;
      }
    }
  }
);

export const systemStateSlice = createSlice({
  name: "systemState",
  initialState,
  reducers: {
    //U1 call function to change the logging account
    handleAccountChanged: (state, action) => {
      state.connectedUser = action.payload || "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(connectWallet.fulfilled, (state, action) => {
      state.connectedUser = action.payload;
    });
    builder.addCase(getConnectedUser.fulfilled, (state, action) => {
      state.connectedUser = action.payload;
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    builder.addCase(checkConnectWallet.pending, (state, action) => {
      state.loadingAtBegining = true;
    });
    builder.addCase(checkConnectWallet.fulfilled, (state, action) => {
      state.isInstalled = action.payload;
      if (!action.payload) {
        state.loadingAtBegining = false;
      }
    });
    builder.addCase(getSmartContractAccounts.fulfilled, (state, action) => {
      if (action.payload.length !== 0) {
        state.smartContractOwner = action.payload[0];
        state.oracle = action.payload[1];
      } else {
        state.smartContractOwner = "";
        state.oracle = "";
      }
      state.loadingAtBegining = false;
    });
  },
});

// Action creators are generated for each case reducer function
export const { handleAccountChanged } = systemStateSlice.actions;

export default systemStateSlice.reducer;
